import { useState, useRef, useCallback, useEffect } from "react";
import { RotateCw, Crop, Check, X, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

interface ImageCropperProps {
  imageData: string;
  onConfirm: (croppedImage: string) => void;
  onCancel: () => void;
}

export function ImageCropper({ imageData, onConfirm, onCancel }: ImageCropperProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      drawCanvas();
    };
    img.src = imageData;
  }, [imageData]);

  useEffect(() => {
    drawCanvas();
  }, [rotation, zoom, pan]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const size = 400;
    canvas.width = size;
    canvas.height = size;

    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.translate(size / 2 + pan.x, size / 2 + pan.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    const scale = Math.min(size / img.width, size / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
  }, [rotation, zoom, pan]);

  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onConfirm(canvas.toDataURL("image/jpeg", 0.85));
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handlePointerUp = () => setIsDragging(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-ink-muted">
          Adjust Image
        </span>
        <button onClick={onCancel} aria-label="Cancel image adjustment" className="text-xs text-ink-muted hover:text-ink transition-colors">
          Cancel
        </button>
      </div>

      <div
        ref={containerRef}
        className="relative rounded-xl overflow-hidden bg-ink/5 flex items-center justify-center aspect-square cursor-grab active:cursor-grabbing touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        role="img"
        aria-label="Image crop and rotate preview. Drag to reposition the photo."
      >
        <canvas ref={canvasRef} className="w-full h-full object-contain" aria-label="Adjusted problem photo preview" />
        {/* Crop guide overlay */}
        <div className="absolute inset-4 border-2 border-dashed border-neon-yellow/50 rounded-lg pointer-events-none" />
        <div className="absolute top-5 left-5 size-3 scan-corner-tl" />
        <div className="absolute top-5 right-5 size-3 scan-corner-tr" />
        <div className="absolute bottom-5 left-5 size-3 scan-corner-bl" />
        <div className="absolute bottom-5 right-5 size-3 scan-corner-br" />
      </div>

      {/* Controls */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <ZoomOut className="size-4 text-ink-muted shrink-0" />
          <Slider
            value={[zoom]}
            min={0.5}
            max={3}
            step={0.1}
            onValueChange={([v]) => setZoom(v)}
            className="flex-1"
          />
          <ZoomIn className="size-4 text-ink-muted shrink-0" />
        </div>

        <div className="flex gap-2">
          <Button variant="subtle" size="sm" className="flex-1" onClick={handleRotate} aria-label="Rotate image 90 degrees clockwise">
            <RotateCw className="size-3.5" /> Rotate 90°
          </Button>
          <Button variant="subtle" size="sm" className="flex-1" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); setRotation(0); }} aria-label="Reset image adjustment">
            <Crop className="size-3.5" /> Reset
          </Button>
        </div>

        <div className="flex gap-2">
          <Button variant="subtle" size="full" className="flex-1" onClick={onCancel} aria-label="Cancel image adjustment">
            <X className="size-4" /> Cancel
          </Button>
          <Button variant="scan" size="full" className="flex-1 font-[family-name:var(--font-display)]" onClick={handleConfirm} aria-label="Use adjusted image">
            <Check className="size-4" /> Use This
          </Button>
        </div>
      </div>
    </div>
  );
}
